import clsx from 'clsx'
import { usePostHog } from 'posthog-js/react'
import { Dispatch, SetStateAction } from 'react'

import { CHART_COLORS } from '../../../constants/chart'

interface LineChartLegendProps {
  dataKeys: string[]
  visibility: number[]
  setVisibility: Dispatch<SetStateAction<number[]>>
  classes?: string
  colors?: Record<string, string>
}

export default function LineChartLegend({
  classes = 'flex gap-2 justify-center flex-wrap w-full mt-1',
  colors,
  dataKeys,
  setVisibility,
  visibility,
}: Readonly<LineChartLegendProps>) {
  const posthog = usePostHog()

  const handleMouseEnter = (i: number) => {
    if (!visibility[i]) return

    setVisibility((prevState: number[]) =>
      prevState.map((visible, index) => {
        if (index === i && visible > 0) return 1
        else if (visible) return 0.1
        else return 0
      }),
    )
  }

  const handleMouseLeave = () => {
    setVisibility((prevState: number[]) =>
      prevState.map(visible => {
        if (visible !== 0) return 1
        else return 0
      }),
    )
  }

  const handleClick = (i: number) => {
    setVisibility(prevState => {
      const isHiding = i >= 0 && i < prevState.length && prevState[i] === 1

      // If dataKeys has a length greater than state then we know there are estimated keys
      const hasEstimatedKeys = prevState.length !== dataKeys.length

      // If there are estimated keys, we need to check if the last visible line is being hidden
      if (hasEstimatedKeys) {
        const slicedPrevState = prevState.slice(0, dataKeys.length)
        const isLastVisibleLine = slicedPrevState.filter(visible => visible !== 0).length === 1

        // Don't allow hiding the last visible line
        if (isHiding && isLastVisibleLine) {
          return prevState
        }
      }

      // Don't allow hiding the last visible line
      if (isHiding && prevState.filter(visible => visible !== 0).length === 1) {
        return prevState
      }

      return prevState.map((visible, index) => {
        const reversedClicked = visible === 1 ? 0 : 1
        return index === i ? reversedClicked : visible
      })
    })

    posthog.capture('LineChartLegend click')
  }

  return (
    <div className={clsx('mb-0.5 ml-4 gap-x-5 gap-y-2 w-full text-xs', classes)}>
      {dataKeys.map((key, index) => (
        <button
          key={key}
          onMouseEnter={() => handleMouseEnter(index)}
          onMouseLeave={() => handleMouseLeave()}
          onClick={() => handleClick(index)}
          className="cursor-pointer inline-flex flex-row gap-1 items-center"
        >
          <div
            className="w-4 h-3"
            style={{
              backgroundColor: visibility[index]
                ? (colors?.[key] ?? CHART_COLORS[index % CHART_COLORS.length])
                : '#504F57',
            }}
          />
          <span
            style={{
              color: visibility[index] ? (colors?.[key] ?? CHART_COLORS[index % CHART_COLORS.length]) : '#504F57',
            }}
          >
            {key}
          </span>
        </button>
      ))}
    </div>
  )
}
